<template>
  <div>
    <AppHeader />
    <div class="barcode-segmentation-container">
      <div class="header">
        <h2>Barcode Segmentation - {{ branchName }}</h2>
        <button @click="goBack" class="btn btn-secondary">
          <i class="bi bi-arrow-left"></i> Back to Branches
        </button>
      </div>

      <div class="segmentation-form">
        <div class="row mb-3">
          <div class="col-md-6">
            <label for="leadCharacters" class="form-label">Lead Characters</label>
            <input 
              type="number" 
              class="form-control" 
              id="leadCharacters" 
              v-model="segmentation.lead_characters"
              min="0"
            >
          </div>
          <div class="col-md-6">
            <label for="itemCodeCharacters" class="form-label">Item Code Characters</label>
            <input 
              type="number" 
              class="form-control" 
              id="itemCodeCharacters" 
              v-model="segmentation.item_code_characters"
              min="0"
            >
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="centralLead" class="form-label">Central Lead</label>
            <input 
              type="text" 
              class="form-control" 
              id="centralLead" 
              v-model="segmentation.central_lead"
              maxlength="1"
            >
          </div>
          <div class="col-md-6">
            <label for="value" class="form-label">Value</label>
            <input 
              type="number" 
              class="form-control" 
              id="value" 
              v-model="segmentation.value"
              min="0"
            >
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="endingLead" class="form-label">Ending Lead</label>
            <input 
              type="text" 
              class="form-control" 
              id="endingLead" 
              v-model="segmentation.ending_lead"
              maxlength="1"
            >
          </div>
        </div>

        <div class="actions mt-4">
          <button @click="saveSegmentation" class="btn btn-primary mr-3" :disabled="loading">
            <span v-if="loading">
              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Saving...
            </span>
            <span v-else>
              <i class="bi bi-save"></i> Save
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BarcodeSegmentation',
  data() {
    return {
      segmentation: {
        branchId: '',
        lead_characters: 0,
        item_code_characters: 0,
        central_lead: '',
        value: 0,
        ending_lead: ''
      },
      branchId: this.$route.params.id,
      branchName: '',
      loading: false,
      existingRecordId: null
    }
  },
  async created() {
    await this.fetchBranchDetails();
    await this.fetchBarcodeSegmentation();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    async fetchBranchDetails() {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/branches/${this.branchId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch branch details');
        }

        const data = await response.json();
        this.branchName = data.data.name;
      } catch (error) {
        console.error('Error fetching branch details:', error);
        this.$toast.error('Failed to load branch details');
      }
    },
    
    async fetchBarcodeSegmentation() {
      try {
        this.loading = true;
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/barcode-segs?branchId=${this.branchId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch barcode segmentation');
        }

        const data = await response.json();
        if (data.data && data.data.length > 0) {
          // If record exists for this branch, populate the form
          const existingData = data.data[0];
          this.existingRecordId = existingData.id;
          this.segmentation = {
            branchId: this.branchId,
            lead_characters: existingData.lead_characters,
            item_code_characters: existingData.item_code_characters,
            central_lead: existingData.central_lead,
            value: existingData.value,
            ending_lead: existingData.ending_lead
          };
        } else {
          // Initialize with branchId if no record exists
          this.segmentation.branchId = this.branchId;
        }
      } catch (error) {
        console.error('Error fetching barcode segmentation:', error);
        this.$toast.error('Failed to load barcode segmentation');
      } finally {
        this.loading = false;
      }
    },
    
  async saveSegmentation() {
//   if (!this.$hasPermission('create barcode segments') && !this.$hasPermission('edit barcode segments')) {
//     this.$toast.error('Not Permitted');
//     return;
//   }

  try {
    this.loading = true;
    const token = localStorage.getItem('authToken');
    const url = this.existingRecordId 
      ? `${this.$BASE_API_URL}/api/barcode-segs/${this.existingRecordId}`
      : `${this.$BASE_API_URL}/api/barcode-segs`;
    
    const method = this.existingRecordId ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(this.segmentation)
    });

    if (!response.ok) {
      throw new Error('Failed to save barcode segmentation');
    }

    // Remove the unused data variable by not assigning the response
    await response.json(); // Still parse the response but don't assign it
    this.$toast.success('Barcode segmentation saved successfully!');
    this.goBack();
  } catch (error) {
    console.error('Error saving barcode segmentation:', error);
    this.$toast.error('Failed to save barcode segmentation');
  } finally {
    this.loading = false;
  }
}
  }
}
</script>

<style scoped>
.barcode-segmentation-container {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.segmentation-form {
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.form-label {
  font-weight: 500;
  margin-bottom: 5px;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header button {
    margin-top: 10px;
  }
}
</style>